import Controller, { OnAfterFetch, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../../../BaseController';
import { ReservedBloodProductsStartAliases as BloodAliases, FragmentNames } from '../../../../../codegen';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { BloodManagementDevice, ReservedProduction, UnityMeasure } from '../../blood-management/blood-management-types';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import moment from 'moment';
import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, MapperAPI, PanelAPI, TextAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import MedicationService from '../../../services/medication-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { ResponseBodyProps } from '../../enteral-nutrition/enteral-nutrition-type';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { ActionType, AdminType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { AbortableReadEvent, ClickEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { UnitMeasures, UnitMeasuresDose } from '../../enteral-nutrition/enteral-nutrition-start/enteral-nutrition-start-type';
import { ApplicationRoute } from '../../../../../constants/routes';
import { applicationContext, ParameterValue } from '@philips-internal/oswald-ui-foundation';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { Value } from '@philips-internal/oswald-ui-core';
import { BarcodeUtils } from '../../../../../utils/barcode-utils';
import { DateFilterUtils } from '../../../../../utils/date-filter-utils';
import BarcodeService from '../../../../../services/barcode-service';

@Controller({ component: BloodAliases.RESERVED_BLOOD_PRODUCT_LAYOUT, fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_START })
export default class ReservedBloodProductsStartController extends BaseController {

  @Inject(BloodAliases.NAME_TEXT)
  private nameTextApi!: TextAPI;

  @Inject(BloodAliases.DATE_TIME_TEXT)
  private dateTimeTextApi!: TextAPI;

  @Inject(BloodAliases.NEW_DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @Inject(BloodAliases.FORM)
  private formApi!: FormAPI;

  @Inject(BloodAliases.DEVICE_MAPPER)
  private deviceMapperApi!: MapperAPI;

  @Inject(BloodAliases.UNITY_FIELD)
  private unityFieldApi!: FieldAPI;

  @Inject(BloodAliases.INFUSION_FORM_FIELD)
  private infusionFormFieldApi!: FieldAPI;

  @Inject(BloodAliases.DEVICE_FIELD)
  private deviceFieldApi!: FieldAPI;

  @Inject(BloodAliases.BUTTON)
  private startButtonApi!: ButtonAPI;

  @Inject(BloodAliases.CARD_DECK)
  private cardDeckApi!: DeckAPI;

  @Inject(BloodAliases.CARDS_ACCORDION_PANEL)
  private cardsAccordionPanelApi!: PanelAPI;

  @Inject(BloodAliases.NAME_DATE_PANEL)
  private nameDatePanelApi!: PanelAPI;

  @Inject(BloodAliases.BARCODE_PANEL_BAG)
  private barcodePanelBagApi!: PanelAPI

  @Inject(BloodAliases.BARCODE_BAG)
  private barcodeBagApi!: BarcodeAPI;

  @Inject(BloodAliases.NOTES_TEXT_AREA)
  private notesTextAreaApi!: TextAreaAPI;

  @OnMount(BloodAliases.RESERVED_BLOOD_PRODUCT_LAYOUT)
  handleBloodStartLayouMount(): void {
    const reservedProduct = this.context.getBulkData();
    this.context.set({ reservedProduct: { ...reservedProduct } });
    this.nameDatePanelApi.setVisible(true);
    this.barcodePanelBagApi.setVisible(true);
  }

  @OnMount(BloodAliases.FORM)
  handleMountForm(): void {
    InformationBarUtils.collapsedBar = true;
    AdminByScheduleParams.readParam = { noCardsRead: true };
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    this.context.set({
      hasDevice: displayDevices === YesNoType.NO,
      materialCode: reservedProduct.id,
    });
  }

  @OnMount(BloodAliases.NEW_DATE_INPUT)
  handleMountNewDateInput(): void {
    const newDate = moment().utc().format(ISO_FULL_DATE_FORMAT);
    this.newDateInputApi.setValue(newDate);
    this.context.set({ currentTime: newDate, dateTime: newDate });
  }

  @OnMount(BloodAliases.DATE_TIME_TEXT)
  handleDateTimeText(): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    this.dateTimeTextApi.setValue(moment(reservedProduct.reservationDate).utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnMount(BloodAliases.NAME_FIELD)
  handleMountMaterialNameField({ target }: EventData<FieldAPI>): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    target.setValue(reservedProduct.reservationDescription);
    this.nameTextApi.setValue(reservedProduct.reservationDescription);
  }

  @OnMount(BloodAliases.BLOOD_TYPING_FIELD)
  handleBloodTypingFieldMount({ target }: EventData<FieldAPI>): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    target.setVisible(!!reservedProduct.bloodTyping);
    target.setData({bloodTyping: reservedProduct.bloodTyping});
  }

  @OnMount(BloodAliases.INFUSION_FORM_AUTOCOMPLETE)
  handleMountInfusionFormAutocomplete(): void {
    this.fetchInfusionForm().then((response) => {
      const currentRecord = this.formApi.getRecord();
      const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;

      this.formApi.setRecord({
        ...currentRecord,
        infusionType: {
          unitMeasure: response[0].unitMeasure,
          unitMeasureValue: response[0].unitMeasureValue
        },
        unitMeasureBlood: {
          unitMeasure: reservedProduct.unitMeasurementDescription,
          unitMeasureValue: reservedProduct.unitMeasurement,
        }
      });
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  @OnMount(BloodAliases.DEVICE_AUTOCOMPLETE)
  handleMountDeviceAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    if (this.shouldDisplayDevices()) {
      target.setEntry('description');
      target.setPrimaryKey('description');
    }
  }

  @OnMount(BloodAliases.DEVICE_MAPPER)
  handleMountDeviceMapper(): void {
    if (this.shouldDisplayDevices()) {
      this.deviceMapperApi.setMap({ value: 'description' });
    }
  }

  @OnMount([BloodAliases.INFUSION_FORM_FIELD, BloodAliases.DEVICE_FIELD])
  handleMountDeviceField({ target }: EventData<FieldAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    target.setVisible(itemStatus === InfusionItemStatus.NOT_STARTED);
  }

  @OnMount(BloodAliases.DEVICE_FETCH)
  handleMountDeviceFetch({ target }: EventData<FetchAPI>): void {
    if (this.shouldDisplayDevices()) {
      const { encounterId } = this.historyService.getCurrentRouteParams();
      const deviceStatus = FoundationUtils
        .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
      this.context.set({ deviceStatus, encounterId });
      target.setEndpoint(Endpoint.EMAR_FIND_ADULT_TPN_DEVICE);
    }
  }

  @OnMount(BloodAliases.VOLUME_STEP_FIELD)
  handleMountVolumeStepField({ target }: EventData<FieldAPI>): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    const { volume } = reservedProduct;
    target.setValue(volume ?? '');
  }

  @OnMount(BloodAliases.BUTTON)
  handleMountStartButton(): void {
    this.setStartButtonValue();
    this.validateIfBarcodeAndAccordionCardsIsVisible();
  }

  @OnMount(BloodAliases.BARCODE_BAG)
  handleBarcodeBagMount(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
    this.resetCardBag();
  }

  @OnAfterFetch(BloodAliases.UNITY_FETCH)
  handleAfterFetchUnityFetch(event: FetchEvent): void {
    const unityMeasure = event.data as UnitMeasuresDose[];
    this.unityFieldApi.setVisible(!!unityMeasure.length);
  }

  @OnAfterFetch(BloodAliases.INFUSION_FORM_FETCH)
  handleAfterFetchInfusionFormFetch(event: FetchEvent): void {
    const infusionForm = event.data as UnitMeasures[];
    this.infusionFormFieldApi.setVisible(!!infusionForm.length);
  }

  @OnAfterFetch(BloodAliases.DEVICE_FETCH)
  handleAfterFetchDeviceFetch(event: FetchEvent): void {
    const deviceFetchApi = event.target as unknown as FetchAPI;
    const devices = event.data as BloodManagementDevice[];

    deviceFetchApi.setParameters({ loading: false }, { skip: true });
    deviceFetchApi.clearPersistedState();

    this.deviceFieldApi.setVisible(!!devices.length);
  }

  @OnClick(BloodAliases.BUTTON)
  async handleClickStartButton(): Promise<void> {
    const isValid = await this.formApi.validate();
    if (isValid) {
      this.submitBlood();
    }
  }

  @OnClick(BloodAliases.CARD)
  handleClickCard(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence;
    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetCardBagDisableStartButton);
  }

  @OnReadSuccess(BloodAliases.BARCODE_BAG)
  handleBagReadSuccessBarcode(event: AbortableReadEvent<BarcodeData>): void {
    const bagRead = event.item.value.trim();

    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    const reservedProductionRead = reservedProduct.bagNumber === bagRead;
    if (reservedProductionRead) {
      if (this.isExpirationDateValid(reservedProduct.endDate)) {
        MedicationService.showToast(MessageService.translate('txt.bag-read-with-success'), ToastLevel.SUCCESS);
        this.startButtonApi.setEnabled(true);
        this.barcodeBagApi.setEnabled(false);
        this.setProductionInCardBag(true);
        return;
      }
      MedicationService.showToast(MessageService.translate('txt.blood-bag-expired'), ToastLevel.ERROR);
      return;
    }

    MedicationService.showToast(MessageService.translate('txt.invalid-bag-code'), ToastLevel.ERROR);
  }

  private isExpirationDateValid(endDate: string): boolean {
    return DateFilterUtils.dateIsAfterNow(endDate);
  }

  private validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const checkMedicationBeforeAdmin = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const isRestartSolution = this.context.get('actionType') === ActionType.RESTART_SOLUTION;

    if (this.checkDisableBarcode(checkMedicationBeforeAdmin, isRestartSolution)) {
      this.barcodePanelBagApi.setVisible(false);
      this.cardsAccordionPanelApi.setVisible(false);
      this.startButtonApi.setEnabled(true);
      return;
    }
  }

  private checkDisableBarcode(checkMedicationBeforeAdmin: ParameterValue|null, isRestartSolution: boolean): boolean {
    return (checkMedicationBeforeAdmin === YesNoType.NO || isRestartSolution);
  }

  resetCardBag = (): void => {
    this.setProductionInCardBag(false);
    this.barcodeBagApi.setEnabled(true);
  }

  resetCardBagDisableStartButton = (): void => {
    this.resetCardBag();
    this.startButtonApi.setEnabled(false);
  }

  private setProductionInCardBag(bagRead: boolean): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    const textMilliliter = MessageService.translate('tws.measure-label');

    const bloodBag = {
      barcode: '',
      endDate: reservedProduct.endDate,
      materialDescription: reservedProduct.description,
      read: bagRead,
      scheduleSequence: reservedProduct.id,
      status: reservedProduct.status,
      unitMeasureCode: textMilliliter,
      volume: reservedProduct.volume
    }

    this.cardDeckApi.setData([
      bloodBag
    ]);
  }

  private submitBlood(): void {
    const requestBody = this.getBloodRequestBody();

    this.startButtonApi.setEnabled(false);

    HttpService.post(Endpoint.EMAR_START_BLOOD, {bag: ''}, { body: requestBody })
      .then((response) => {
        this.showResponseMessage(response.body as string);
        this.redirectToTimeline();
      })
      .catch((error) => this.handleApiError(error))
      .finally(() => {
        this.startButtonApi.setEnabled(true);
      });
  }

  private getBloodRequestBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    const formRecord = this.formApi.getRecord();
    const currentTime = this.newDateInputApi.getValue();
    const actionType = this.context.get('actionType');
    const infusionTypeForm = formRecord.infusionType as { unitMeasure?: string, unitMeasureValue?: string };
    const infusionType = this.getInfusionType(infusionTypeForm.unitMeasureValue)
    const device = formRecord.device as BloodManagementDevice;
    const unityMeasure = formRecord.unitMeasureBlood as UnityMeasure;

    const body = {
      actionType,
      bagNumber: reservedProduct.bagNumber,
      changeDate: currentTime,
      dateTime: reservedProduct.reservationDate,
      deviceSequence: Number(device?.deviceCode),
      dosageType: unityMeasure?.unitMeasureCode,
      encounterNumber: encounterId,
      infusionSpeedQuantity: formRecord?.infusionSpeed,
      infusionType,
      justifyDescription: this.notesTextAreaApi.getValue() as string,
      observationDescription: formRecord.observation ?? '',
      prescriptionNumber: Number(reservedProduct.prescriptionNumber),
      reservationProductSequenceNumber: reservedProduct.id,
      reservationSequenceNumber: reservedProduct.reservationId,
      solutionSequence: 0,
      volumePhaseQuantity: Number(String(formRecord.volume).split(' ')[0])
    } as Record<string, Value>;

    return body;
  }

  private getInfusionType(unitMeasureValue?: string): AdminType {
    return unitMeasureValue !== AdminType.OPEN && unitMeasureValue !== AdminType.TIME &&
      unitMeasureValue !== AdminType.INFUSION_SPEED ? AdminType.INFUSION_SPEED : unitMeasureValue;
  }

  private setStartButtonValue(): void {
    const reservedProduct = this.context.get('reservedProduct') as ReservedProduction;
    this.startButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));

    if (reservedProduct.status === InfusionHourStatus.INTERRUPTED) {
      this.context.set({ actionType: ActionType.RESTART_SOLUTION });
      this.startButtonApi.setValue(MessageService.translate('txt.restart-transfusion'));
      return;
    }

    if (reservedProduct.status !== InfusionHourStatus.STARTED) {
      this.context.set({ actionType: ActionType.START_SOLUTION });
      this.startButtonApi.setValue(MessageService.translate('txt.start-transfusion'));
      return;
    }
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    const actionType = this.context.get('actionType');
    switch (actionType) {
      case ActionType.START_SOLUTION:
        MedicationService.showToast(MessageService.translate('txt.item-started-with-success'), ToastLevel.SUCCESS);
        break;
      case ActionType.RESTART_SOLUTION:
        MedicationService.showToast(MessageService.translate('txt.item-restarted-with-success'), ToastLevel.SUCCESS);
        break;
      default:
        break;
    }
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_START);
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_VIEWER);
  }

  private fetchInfusionForm(): Promise<ResponseBodyProps[]> {
    const infusionFormDomainSequence = 1883;
    const activeSituation = 'A';

    return HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain: infusionFormDomainSequence, situation: activeSituation })
      .then(response => response.body as ResponseBodyProps[]);
  }

  private shouldDisplayDevices(): boolean {
    const displayDevices = FoundationUtils
      .findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
    return displayDevices === YesNoType.YES;
  }

  private handleApiError(error: any): void {
    if (error.body && error.body.errors) {
      error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
    }
  }
}
